import React, { memo, useCallback, useEffect, useReducer, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'

import { useTheme } from '@material-ui/styles'

import { TYPES } from '~/actions'
import track from '~/track'
import StoreDetails from '../StoreDetails'
import useViewportSize from '~/components/useViewportSize'
import RenderDesktop from '~/components/Desktop'
import RenderMobile from '~/components/Mobile'
import ProductList from '~/components/ProductList'
import StoreList from '~/components/StoreList'
import HomePage from '~/components/HomePage'
import { useQueryParamsPersistence } from '~/components/usePersistentQueryParams'

import MapContainer from '../Map'
import StoreDetailsAlone from '../StoreDetails/StoreDetailsAlone'

import Logo from '~/assets/new_gofind/logoMap.svg'

const useStyles = makeStyles(() => ({
  AppContainer: {},
  logoGofind: {
    zIndex: 99,
    position: 'absolute',
    bottom: 20,
    right: 8,
    width: 100,
  },
}))

const selector = createSelector(
  (state) => state.app.view,
  (state) => state.app.mobileBottomShow,
  (state) => state.app.currentProduct,
  (state) => state.app.showStoreInformation,
  (state) => state.app.stores,
  (...args) => args,
)

function reducer(state, { type, ...payload }) {
  return { ...state, ...payload }
}

function AppContainer({ position, config }) {
  useQueryParamsPersistence()

  const classes = useStyles()
  const viewport = useViewportSize()
  const theme = useTheme()

  const [view, showBottom, currentProduct, showStoreInformation, stores] = useSelector(selector, shallowEqual)

  const [storeHover, setStoreHover] = useState(0)
  const [isMobile, setIsMobile] = useState(viewport.width < theme.gofind.desktopMaxWidth)

  const urlOrigin = window.location.toString()
  const isLowHeight = viewport.height < 700

  const [state, dispatch] = useReducer(reducer, {
    dragActive: false,
    dragEnd: true,
    initial: 0,
    current: 0,
    offset: 0,
  })

  const getMapSize = (viewport) => viewport.height * (isLowHeight ? 0.5 : 0.6) + 56

  const handleToggle = useCallback(() => {
    if (isMobile) {
      if (state.current > 0 || view === TYPES.VIEW_PRODUCTS) {
        dispatch({ current: 0, offset: 0 })
        return
      }

      dispatch({ current: getMapSize(viewport), offset: getMapSize(viewport) })

      if (view === 'VIEW_PRODUCTS') return track('clickMapaProdutos', { category: 'mapa', name: 'products' })

      return track('clickMapaLoja', { category: 'mapa', name: 'store' })
    }
  }, [isMobile, state.current, view, viewport, dispatch])

  useEffect(() => {
    if (!stores || !stores?.length) dispatch({ current: getMapSize(viewport), offset: getMapSize(viewport) })
    if (stores?.length > 0) dispatch({ current: 0, offset: 0 })
  }, [stores])

  useEffect(() => {
    if (!showStoreInformation && state.current > 0) dispatch({ current: 0, offset: 0 })
  }, [showStoreInformation])

  useEffect(() => setIsMobile(viewport.width < theme.gofind.desktopMaxWidth), [viewport])

  useEffect(() => {
    if (view === TYPES.VIEW_PRODUCTS) dispatch({ current: 0, offset: 0 })

    window.addEventListener('beforeinstallprompt', (e) => {
      track('PWA_USER_INSTALL_ASKED')

      e.userChoice.then((choiceResult) => {
        track('PWA_USER_INSTALL_CHOICERESULT', { name: choiceResult.outcome })
      })
    })
  }, [])

  useEffect(() => {
    const vp = document.querySelector('meta[name=viewport]')

    vp.setAttribute(
      'content',
      `width=device-width, initial-scale=1.0, user-scalable=0, minimum-scale=1.0, maximum-scale=1.0, viewport-fit=cover`,
    )
  }, [])

  if (view === TYPES.VIEW_TERM) return

  if (view === TYPES.VIEW_HOME_PAGE) {
    return <HomePage position={position} />
  }

  if (view === TYPES.VIEW_STORE_DETAILS_ALONE) {
    return <StoreDetailsAlone config={config} position={position} />
  }

  const children = (
    <>
      {[TYPES.VIEW_PRODUCTS, TYPES.VIEW_SEARCH_PRODUCTS].indexOf(view) !== -1 && <ProductList position={position} />}

      {view === TYPES.VIEW_STORES && (
        <StoreList
          currentProduct={currentProduct}
          storeHover={storeHover}
          setHover={setStoreHover}
          position={position}
          handleToggle={handleToggle}
        />
      )}

      {view === TYPES.VIEW_STORE_DETAILS && (
        <StoreDetails position={position} config={config} handleToggle={handleToggle} />
      )}
    </>
  )

  const defaultProps = {
    viewport,
    view,
    storeHover,
    setStoreHover,
    children,
    position,
    config,
  }

  return (
    <div className={classes.AppContainer} style={{ height: viewport.height }}>
      {!isMobile && <RenderDesktop {...defaultProps} />}

      {isMobile && <RenderMobile {...defaultProps} state={state} dispatch={dispatch} handleToggle={handleToggle} />}

      {position.user?.lat && position.user?.lng ? (
        <MapContainer
          showBottom={showBottom}
          view={view}
          position={position}
          showMapPlaceholder={position.showMapPlaceholder}
          mobile={isMobile}
          storeHover={storeHover}
          setStoreHover={setStoreHover}
          state={state}
          dispatch={dispatch}
          config={config}
          currentProduct={currentProduct}
        />
      ) : !urlOrigin.includes('onlineStore') ? (
        <img
          src="https://s3.sa-east-1.amazonaws.com/gofind.img.repo/bg-mapa.webp"
          alt="Preview Map"
          width="100%"
          height="100%"
          style={{ objectFit: 'cover' }}
        />
      ) : null}

      {view === 'VIEW_EMPTY' && isMobile && (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.gofind.online/pertinhodemim?utm_source=Pertinhodemim%20Logo&utm_medium=mapa&utm_campaign=logo%20map"
        >
          <img src={Logo} className={classes.logoGofind} />
        </a>
      )}
    </div>
  )
}

export default memo(AppContainer)
